/*
 * Dýravinir size finder for Hurtta gear ("Stærðarhjálp").
 *
 * One data-driven engine for every sized Hurtta product. The product is
 * identified by its `custom.size_chart_key` metafield; the tables below are
 * transcribed from Hurtta's official size-chart artwork (inRiver Content
 * Store, "Product information/Product Size Charts"), one row per size, in cm.
 *
 * Design rules (see docs/shopify/hurtta-sizing-audit-2026-09-04.md):
 *   - Recommend the size where the dog's measurements sit most centrally in
 *     the adjustment range, never the first row that happens to match.
 *   - Category rules follow Hurtta's own fit guide: back length decides coats
 *     and overalls, chest decides harnesses, neck decides collars, weight
 *     decides the life vest. Between two sizes: coats go up, overalls go down.
 *   - Availability is read from the product's variants, so a size we do not
 *     carry is said so, and a sold-out size is said so.
 *   - Measurements are remembered per measurement name (not per input
 *     position) so they carry correctly from a harness to an overall.
 *
 * The engine (evaluate / recommend / normalizeSize) is pure and is exported
 * for Node so tools/theme/dv_size_finder_check.js can test it.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (root) {
    root.DvSizeFinder = api;
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { api.initAll(); });
      } else {
        api.initAll();
      }
    }
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var CM_PER_INCH = 2.54;
  var KG_PER_LB = 0.45359237;
  var STORAGE_DOG = 'dv_sf_dog';
  var STORAGE_OWNER = 'dv_sf_owner';
  var STORAGE_UNIT = 'dv_sf_unit';

  /* ------------------------------------------------------------------ */
  /* Measurements                                                        */
  /* ------------------------------------------------------------------ */

  var MEASURES = {
    back: {
      label: 'Baklengd',
      unit: 'len',
      store: 'dog',
      diagram: 1,
      hint: 'Mældu ofan á bakinu frá hnakkagróf (þar sem hálsinn mætir herðunum) að skottrót, með hundinn standandi.'
    },
    neck: {
      label: 'Hálsmál',
      unit: 'len',
      store: 'dog',
      diagram: 2,
      hint: 'Ummál neðst á hálsinum, þar sem hálsól situr eðlilega.'
    },
    chest: {
      label: 'Brjóstmál',
      unit: 'len',
      store: 'dog',
      diagram: 3,
      hint: 'Ummál um breiðasta hluta brjóstkassans, rétt fyrir aftan framfæturna.'
    },
    foreleg: {
      label: 'Framfótur',
      unit: 'len',
      store: 'dog',
      diagram: 5,
      hint: 'Frá olnboga niður að úlnlið, rétt fyrir ofan framloppuna.'
    },
    hindleg: {
      label: 'Afturfótur',
      unit: 'len',
      store: 'dog',
      diagram: 6,
      hint: 'Frá hné niður að hækli.'
    },
    weight: {
      label: 'Þyngd hundsins',
      unit: 'mass',
      store: 'dog',
      hint: 'Þyngd hundsins í kílóum.'
    },
    hchest: {
      label: 'Brjóstmál (þitt)',
      unit: 'len',
      store: 'owner',
      hint: 'Ummál um breiðasta hluta brjóstkassans á þér.'
    },
    hwaist: {
      label: 'Mittismál (þitt)',
      unit: 'len',
      store: 'owner',
      hint: 'Ummál um mittið.'
    },
    hhem: {
      label: 'Mjaðmamál (þitt)',
      unit: 'len',
      store: 'owner',
      hint: 'Ummál um mjaðmirnar, þar sem faldur flíkurinnar endar.'
    }
  };

  /* ------------------------------------------------------------------ */
  /* Categories and their fit rules                                      */
  /* ------------------------------------------------------------------ */

  var CATEGORIES = {
    overall: {
      name: 'heilgalli',
      primary: 'back',
      between: 'smaller',
      whoToMeasure: 'dog',
      advice: 'Baklengdin ræður stærð heilgalla. Ef hundurinn mælist á mörkum tveggja stærða og er virkur skaltu velja minni stærðina svo gallinn sitji þétt og haldist á sínum stað.'
    },
    coat: {
      name: 'jakki',
      primary: 'back',
      between: 'larger',
      whoToMeasure: 'dog',
      advice: 'Baklengdin ræður stærð jakka. Á mörkum tveggja stærða skaltu velja stærri stærðina – jakkinn er stillanlegur í baklengd og helst á sínum stað án þess að hefta hreyfingar.'
    },
    harness: {
      name: 'beisli',
      primary: 'chest',
      between: 'center',
      whoToMeasure: 'dog',
      advice: 'Brjóstmálið er mikilvægasta málið fyrir beisli. Veldu stærðina þar sem brjóstmálið lendir um miðbik bilsins – þá er hægt að stilla í báðar áttir.'
    },
    collar: {
      name: 'hálsól',
      primary: 'neck',
      between: 'center',
      whoToMeasure: 'dog',
      advice: 'Aðeins hálsmálið skiptir máli. Veldu stærðina þar sem hálsmálið lendir um miðbik bilsins svo ólin sé stillanleg í báðar áttir.'
    },
    lifevest: {
      name: 'björgunarvesti',
      primary: 'weight',
      between: 'center',
      whoToMeasure: 'dog',
      advice: 'Þyngd hundsins ræður stærð björgunarvestisins; háls- og brjóstmál staðfesta að það sitji rétt.'
    },
    human: {
      name: 'flík fyrir þig',
      primary: 'hchest',
      between: 'larger',
      whoToMeasure: 'owner',
      advice: 'Þetta er flík fyrir þig, ekki hundinn. Brjóstmálið ræður stærðinni; mittis- og mjaðmamál staðfesta hana.'
    }
  };

  var FIT_LETTERS = {
    XS: 'Sérstök stærð fyrir langbakaðar og stuttfættar tegundir, t.d. dachshund.',
    S: 'Grunnstærð fyrir litla, granna og stuttfætta hunda, t.d. tíbetskan spaniel og Jack Russell terrier.',
    M: 'Grunnstærð fyrir hunda með meðallangan háls, bringu og fætur, t.d. schnauzer og labrador retriever.',
    L: 'Sérstök stærð fyrir hunda með djúpa bringu eða mjóan líkama og langa fætur, t.d. púðla og whippet.',
    XL: 'Sérstök stærð fyrir hunda með þykkan háls og breiða bringu, t.d. franskan bulldog og bull terrier.'
  };

  /* ------------------------------------------------------------------ */
  /* Size tables (cm), transcribed from Hurtta's official charts          */
  /* ------------------------------------------------------------------ */

  function rows(measures, table) {
    return table.map(function (r) {
      var ranges = {};
      for (var i = 0; i < measures.length; i++) ranges[measures[i]] = r[i + 1];
      return { size: r[0], ranges: ranges };
    });
  }

  var OVERALL_MEASURES = ['back', 'neck', 'chest', 'foreleg', 'hindleg'];
  var OVERALL_TABLE = rows(OVERALL_MEASURES, [
    ['25S', [22, 27], [20, 30], [30, 40], [6, 11], [8, 13]],
    ['30S', [27, 32], [20, 35], [35, 45], [8, 13], [11, 16]],
    ['30M', [27, 32], [25, 40], [40, 50], [10, 15], [13, 20]],
    ['35S', [32, 37], [25, 45], [40, 55], [9, 15], [12, 20]],
    ['35M', [32, 37], [25, 45], [45, 60], [10, 17], [15, 24]],
    ['40M', [37, 42], [30, 50], [50, 65], [13, 20], [17, 28]],
    ['45M', [42, 47], [35, 55], [55, 70], [15, 23], [20, 30]],
    ['50M', [47, 52], [35, 55], [60, 75], [17, 28], [23, 34]],
    ['55M', [52, 57], [40, 60], [60, 80], [18, 29], [24, 35]],
    ['60M', [57, 62], [45, 60], [65, 85], [19, 30], [25, 36]],
    ['65M', [62, 67], [45, 65], [70, 90], [21, 33], [28, 41]]
  ]);

  var COAT_MEASURES = ['back', 'neck', 'chest'];
  var MUDVENTURE_COAT_TABLE = rows(COAT_MEASURES, [
    ['25', [22, 27], [20, 30], [30, 40]],
    ['30', [27, 32], [25, 40], [40, 55]],
    ['35', [32, 37], [25, 45], [40, 60]],
    ['40', [37, 42], [30, 50], [45, 65]],
    ['45', [42, 47], [30, 50], [50, 70]],
    ['50', [47, 52], [35, 55], [55, 75]],
    ['55', [52, 57], [40, 55], [60, 80]],
    ['60', [57, 62], [40, 60], [65, 85]],
    ['65', [62, 67], [45, 65], [70, 95]]
  ]);

  var HARNESS_MEASURES = ['neck', 'chest'];
  var HUMAN_MEASURES = ['hchest', 'hwaist', 'hhem'];
  var HUMAN_TABLE = rows(HUMAN_MEASURES, [
    ['XS', [80, 86], [65, 70], [89, 95]],
    ['S', [86, 91], [70, 75], [95, 100]],
    ['M', [91, 96], [75, 81], [100, 105]],
    ['L', [96, 102], [81, 87], [105, 112]],
    ['XL', [102, 108], [87, 93], [112, 119]],
    ['XXL', [108, 114], [93, 102], [119, 124]]
  ]);

  var CHARTS = {
    'extreme-heilgalli': {
      category: 'overall',
      title: 'Extreme heilgalli',
      measures: OVERALL_MEASURES,
      sizes: OVERALL_TABLE,
      letters: ['S', 'M'],
      source: 'Hurtta – Dog’s Measurements for Extreme Overall'
    },
    'mudventure-heilgalli': {
      category: 'overall',
      title: 'Mudventure heilgalli ECO',
      measures: OVERALL_MEASURES,
      sizes: OVERALL_TABLE,
      letters: ['S', 'M'],
      source: 'Hurtta – Dog’s Measurements for Mudventure Overall'
    },
    'expedition-parka': {
      category: 'coat',
      title: 'Expedition Parka',
      measures: COAT_MEASURES,
      sizes: rows(COAT_MEASURES, [
        ['20', [16, 22], [20, 25], [25, 35]],
        ['25', [22, 27], [20, 30], [30, 40]],
        ['30', [27, 32], [25, 40], [40, 50]],
        ['30XL', [27, 32], [30, 45], [50, 60]],
        ['35', [32, 37], [25, 45], [40, 60]],
        ['35XL', [32, 37], [40, 50], [50, 65]],
        ['40', [37, 42], [30, 50], [45, 65]],
        ['40XS', [37, 42], [30, 40], [40, 55]],
        ['45', [42, 47], [30, 50], [50, 70]],
        ['45XS', [42, 47], [30, 40], [45, 55]],
        ['50', [47, 52], [35, 55], [55, 75]],
        ['55', [52, 57], [40, 55], [60, 80]],
        ['60', [57, 62], [40, 60], [65, 85]],
        ['65', [62, 67], [45, 65], [70, 90]],
        ['70', [67, 73], [50, 70], [75, 100]],
        ['80', [73, 85], [50, 75], [80, 105]]
      ]),
      letters: ['XS', 'XL'],
      source: 'Hurtta – Dog’s Measurements for Expedition Parka'
    },
    'warming-midlayer': {
      category: 'coat',
      title: 'Warming Midlayer peysa ECO',
      measures: COAT_MEASURES,
      sizes: rows(COAT_MEASURES, [
        ['25', [22, 27], [20, 25], [30, 40]],
        ['30', [27, 32], [20, 30], [35, 45]],
        ['35', [32, 37], [25, 35], [40, 50]],
        ['40', [37, 42], [30, 40], [40, 55]],
        ['45', [42, 47], [30, 40], [45, 60]],
        ['50', [47, 52], [35, 45], [50, 65]],
        ['55', [52, 57], [35, 50], [55, 70]],
        ['60', [57, 62], [40, 55], [60, 75]],
        ['65', [62, 67], [45, 55], [65, 80]]
      ]),
      source: 'Hurtta – Dog’s Measurements for Warming Midlayer ECO'
    },
    'extreme-warmer': {
      category: 'coat',
      title: 'Extreme Warmer jakki ECO',
      measures: COAT_MEASURES,
      sizes: rows(COAT_MEASURES, [
        ['25', [22, 27], [20, 30], [30, 40]],
        ['30', [27, 32], [25, 35], [40, 55]],
        ['35', [32, 37], [25, 45], [40, 60]],
        ['40', [37, 42], [30, 50], [45, 65]],
        ['45', [42, 47], [30, 50], [50, 70]],
        ['50', [47, 52], [35, 55], [55, 75]],
        ['55', [52, 57], [40, 55], [60, 80]],
        ['60', [57, 62], [40, 60], [65, 85]],
        ['65', [62, 67], [45, 60], [70, 90]]
      ]),
      source: 'Hurtta – Dog’s Measurements for Extreme Warmer III ECO'
    },
    'mudventure-jakki': {
      category: 'coat',
      title: 'Mudventure jakki ECO',
      measures: COAT_MEASURES,
      sizes: MUDVENTURE_COAT_TABLE,
      source: 'Hurtta – Dog’s Measurements for Mudventure Coat'
    },
    'mudventure-endurskins': {
      category: 'coat',
      title: 'Mudventure endurskinsjakki',
      measures: COAT_MEASURES,
      sizes: MUDVENTURE_COAT_TABLE,
      source: 'Hurtta – Dog’s Measurements for Mudventure Coat (reflective)'
    },
    'casual-halsol': {
      category: 'collar',
      title: 'Casual hálsól ECO',
      measures: ['neck'],
      sizes: rows(['neck'], [
        ['20-30', [18, 30]],
        ['25-35', [23, 35]],
        ['30-40', [28, 40]],
        ['35-45', [33, 45]],
        ['40-50', [38, 50]],
        ['45-55', [43, 55]]
      ]),
      source: 'Hurtta – Dog’s Measurements for Casual Collar ECO'
    },
    'casual-y-beisli': {
      category: 'harness',
      title: 'Casual Y-beisli ECO',
      measures: HARNESS_MEASURES,
      sizes: rows(HARNESS_MEASURES, [
        ['30-35', [15, 30], [28, 35]],
        ['35-45', [20, 38], [33, 45]],
        ['45-55', [23, 40], [43, 55]],
        ['50-60', [25, 45], [48, 60]],
        ['60-70', [30, 50], [58, 70]],
        ['70-80', [35, 55], [68, 80]],
        ['80-90', [40, 65], [78, 90]],
        ['90-100', [43, 70], [88, 100]]
      ]),
      source: 'Hurtta – Dog’s Measurements for Casual Y-harness ECO'
    },
    'venture-no-pull': {
      category: 'harness',
      title: 'Venture No-Pull beisli',
      measures: HARNESS_MEASURES,
      sizes: rows(HARNESS_MEASURES, [
        ['35-40', [20, 30], [30, 40]],
        ['40-45', [20, 35], [37, 45]],
        ['45-60', [25, 45], [42, 60]],
        ['60-80', [35, 55], [57, 80]],
        ['80-100', [40, 70], [70, 100]]
      ]),
      source: 'Hurtta – Dog’s Measurements for Venture No-Pull Harness'
    },
    /*
     * Weekend Warrior: the shop carries two generations. The product
     * titled "Weekend Warrior Beisli" holds the Harness II ECO size run
     * (40-50 … 75-100, SKUs 9346xx/9347xx) plus the old 100-120 (SKUs
     * 933398/934114); the product titled "Weekend Warrior Beisli ECO" holds
     * the original size run (40-45 … 100-120, SKUs 9334xx). Each key gets
     * the table that matches its variants.
     */
    'weekend-warrior': {
      category: 'harness',
      title: 'Weekend Warrior beisli',
      manualReview: true,
      measures: HARNESS_MEASURES,
      sizes: rows(HARNESS_MEASURES, [
        ['40-50', [20, 35], [37, 50]],
        ['45-60', [30, 45], [42, 60]],
        ['55-70', [35, 50], [52, 70]],
        ['65-80', [35, 55], [62, 80]],
        ['75-100', [40, 65], [72, 100]],
        ['100-120', [50, 75], [97, 120]]
      ]),
      source: 'Hurtta – Dog’s Measurements for Weekend Warrior Harness II ECO (+ 100-120 from the original chart)'
    },
    'weekend-warrior-eco': {
      category: 'harness',
      title: 'Weekend Warrior beisli ECO',
      manualReview: true,
      measures: HARNESS_MEASURES,
      sizes: rows(HARNESS_MEASURES, [
        ['40-45', [20, 40], [37, 45]],
        ['45-60', [25, 45], [42, 60]],
        ['60-80', [35, 60], [57, 80]],
        ['80-100', [40, 70], [77, 100]],
        ['100-120', [50, 75], [97, 120]]
      ]),
      source: 'Hurtta – Dog’s Measurements for Weekend Warrior Harness'
    },
    'life-savior': {
      category: 'lifevest',
      title: 'Life Savior björgunarvesti ECO',
      measures: ['weight', 'neck', 'chest'],
      sizes: rows(['weight', 'neck', 'chest'], [
        ['5-10', [5, 10], [30, 40], [45, 60]],
        ['10-15', [10, 15], [35, 45], [55, 70]],
        ['15-20', [15, 20], [40, 50], [65, 80]],
        ['20-30', [20, 30], [45, 55], [70, 85]],
        ['30-40', [30, 40], [50, 60], [75, 95]]
      ]),
      sizeUnit: 'kg',
      source: 'Hurtta – Dog’s Measurements for Life Savior'
    },
    'training-vesti': {
      category: 'human',
      title: 'Training vesti ECO',
      measures: HUMAN_MEASURES,
      sizes: HUMAN_TABLE,
      garmentLength: { XS: 67, S: 69, M: 71, L: 72, XL: 74, XXL: 76 },
      source: 'Hurtta – Measurements for Training Vest'
    },
    'training-jakki': {
      category: 'human',
      title: 'Training jakki ECO',
      measures: HUMAN_MEASURES,
      sizes: HUMAN_TABLE,
      garmentLength: { XS: 68, S: 70, M: 71, L: 73, XL: 75, XXL: 77 },
      source: 'Hurtta – Measurements for Training Jacket'
    }
  };

  /* ------------------------------------------------------------------ */
  /* Pure engine                                                         */
  /* ------------------------------------------------------------------ */

  /** "40M / 16M" -> "40M", "25S*" -> "25S", "30 XL" -> "30XL", "40-45 cm" -> "40-45", "5-10 kg" -> "5-10" */
  function normalizeSize(label) {
    if (label === null || label === undefined) return '';
    var s = String(label).toUpperCase();
    s = s.replace(/\s*\/.*$/, '');
    s = s.replace(/[\s*"”″]/g, '');
    s = s.replace(/(CM|KG)$/, '');
    return s;
  }

  function isNum(v) {
    return typeof v === 'number' && isFinite(v);
  }

  /**
   * Evaluate every size against the entered measurements (cm / kg).
   * `values` may be partial; only entered measures are judged.
   */
  function evaluate(chart, values) {
    var entered = chart.measures.filter(function (m) { return isNum(values[m]); });
    return chart.sizes.map(function (row) {
      var positions = {};
      var outside = {};
      var totalOutside = 0;
      var centerScore = 0;
      entered.forEach(function (m) {
        var r = row.ranges[m];
        var lo = r[0], hi = r[1], v = values[m];
        var span = Math.max(hi - lo, 1);
        var p = (v - lo) / span;
        positions[m] = p;
        if (v < lo) { outside[m] = v - lo; totalOutside += lo - v; }
        else if (v > hi) { outside[m] = v - hi; totalOutside += v - hi; }
        centerScore += Math.abs(p - 0.5);
      });
      return {
        size: row.size,
        row: row,
        positions: positions,
        outside: outside,
        fits: entered.length > 0 && totalOutside === 0,
        totalOutside: totalOutside,
        centerScore: centerScore,
        entered: entered
      };
    });
  }

  function complete(chart, values) {
    return chart.measures.every(function (m) { return isNum(values[m]); });
  }

  /**
   * Pick a recommendation.
   * availability: { normalizedSize: { carried: bool, available: bool } }
   * Returns { status, recommended, alternatives, nearest, missing }.
   */
  function recommend(chart, values, availability) {
    availability = availability || {};
    if (chart.manualReview) {
      return { status: 'manual', missing: [], recommended: null, alternatives: [], nearest: [] };
    }
    var cat = CATEGORIES[chart.category];
    var evals = evaluate(chart, values);
    var missing = chart.measures.filter(function (m) { return !isNum(values[m]); });
    var fits = evals.filter(function (e) { return e.fits; });

    function avail(e) {
      var a = availability[normalizeSize(e.size)];
      return a || { carried: false, available: false };
    }

    if (missing.length === chart.measures.length) {
      return { status: 'empty', missing: missing, recommended: null, alternatives: [], nearest: [] };
    }

    if (fits.length === 0) {
      var nearest = evals.slice().sort(function (a, b) {
        return a.totalOutside - b.totalOutside || a.centerScore - b.centerScore;
      }).slice(0, 2);
      nearest.forEach(function (e) { e.availability = avail(e); });
      return { status: missing.length ? 'partial-none' : 'none', missing: missing, recommended: null, alternatives: [], nearest: nearest };
    }

    // Primary measure decides; rank fits by how centred the primary sits,
    // then by overall centring across all entered measures.
    var primary = cat.primary;
    var ranked = fits.slice().sort(function (a, b) {
      var pa = isNum(a.positions[primary]) ? Math.abs(a.positions[primary] - 0.5) : 0.5;
      var pb = isNum(b.positions[primary]) ? Math.abs(b.positions[primary] - 0.5) : 0.5;
      if (Math.abs(pa - pb) > 0.05) return pa - pb;
      return a.centerScore - b.centerScore;
    });

    var pick = ranked[0];

    // Between-two-sizes rule on the primary measure: coats go up, overalls down.
    if (isNum(pick.positions[primary]) && cat.between !== 'center') {
      var p = pick.positions[primary];
      var idx = chart.sizes.indexOf(pick.row);
      var neighbourIdx = cat.between === 'larger' ? idx + 1 : idx - 1;
      var neighbour = null;
      for (var i = 0; i < fits.length; i++) {
        if (chart.sizes.indexOf(fits[i].row) === neighbourIdx) neighbour = fits[i];
      }
      var atEdge = cat.between === 'larger' ? p >= 0.8 : p <= 0.2;
      if (atEdge && neighbour) pick = neighbour;
    }

    // Prefer a size we actually carry when the best fit is not in our range
    // but another fitting size is (e.g. Extreme heilgalli above 40M).
    var pickAvail = avail(pick);
    var note = null;
    if (!pickAvail.carried) {
      var carriedFit = null;
      for (var j = 0; j < ranked.length; j++) {
        if (avail(ranked[j]).carried) { carriedFit = ranked[j]; break; }
      }
      if (carriedFit) {
        note = 'not-carried-fallback';
        pick.availability = pickAvail;
        var best = pick;
        pick = carriedFit;
        pick.fallbackFrom = best;
      }
    }
    pick.availability = avail(pick);

    var alternatives = ranked.filter(function (e) { return e !== pick && e !== pick.fallbackFrom; });
    alternatives.forEach(function (e) { e.availability = avail(e); });

    return {
      status: missing.length ? 'partial' : 'ok',
      missing: missing,
      recommended: pick,
      alternatives: alternatives,
      nearest: [],
      note: note
    };
  }

  /** Human-readable reason why an alternative is second choice. */
  function describeFit(chart, e) {
    var parts = [];
    chart.measures.forEach(function (m) {
      var p = e.positions[m];
      if (!isNum(p)) return;
      var label = MEASURES[m].label.replace(/ \(.*\)$/, '').toLowerCase();
      if (p < 0.15) parts.push('rúmt um ' + label);
      else if (p > 0.85) parts.push('þröngt um ' + label);
    });
    return parts.join(', ');
  }

  function describeOutside(chart, e, unit) {
    var parts = [];
    chart.measures.forEach(function (m) {
      var d = e.outside[m];
      if (!isNum(d) || d === 0) return;
      var label = MEASURES[m].label.replace(/ \(.*\)$/, '').toLowerCase();
      var abs = Math.abs(d);
      var shown = MEASURES[m].unit === 'mass'
        ? (unit === 'in' ? (abs / KG_PER_LB).toFixed(1) + ' lb' : abs.toFixed(1).replace(/\.0$/, '') + ' kg')
        : (unit === 'in' ? (abs / CM_PER_INCH).toFixed(1) + '"' : abs.toFixed(1).replace(/\.0$/, '') + ' cm');
      parts.push(label + ' er ' + shown + (d > 0 ? ' yfir hámarki' : ' undir lágmarki'));
    });
    return parts.join(', ');
  }

  /* ------------------------------------------------------------------ */
  /* Storage                                                             */
  /* ------------------------------------------------------------------ */

  function readStore(key) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function writeStore(key, obj) {
    try { localStorage.setItem(key, JSON.stringify(obj)); } catch (e) { /* private mode */ }
  }

  /* ------------------------------------------------------------------ */
  /* UI                                                                  */
  /* ------------------------------------------------------------------ */

  function fmt(v, unit, kind) {
    if (!isNum(v)) return '';
    if (kind === 'mass') {
      return unit === 'in' ? String(Math.round(v / KG_PER_LB * 10) / 10) : String(Math.round(v * 10) / 10);
    }
    return unit === 'in' ? String(Math.round(v / CM_PER_INCH * 4) / 4) : String(Math.round(v * 10) / 10);
  }

  function unitSuffix(unit, kind) {
    if (kind === 'mass') return unit === 'in' ? 'lb' : 'kg';
    return unit === 'in' ? 'tommur' : 'cm';
  }

  function toBase(v, unit, kind) {
    if (!isNum(v)) return null;
    if (unit !== 'in') return v;
    return kind === 'mass' ? v * KG_PER_LB : v * CM_PER_INCH;
  }

  function rangeText(r, unit, kind) {
    return fmt(r[0], unit, kind) + '–' + fmt(r[1], unit, kind);
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null) n.textContent = text;
    return n;
  }

  function sizeDisplay(chart, size) {
    return chart.sizeUnit === 'kg' ? size + ' kg' : size;
  }

  function Finder(modal) {
    this.modal = modal;
    this.key = modal.getAttribute('data-key');
    this.chart = CHARTS[this.key];
    if (!this.chart) return;
    this.cat = CATEGORIES[this.chart.category];
    this.sizeIndex = parseInt(modal.getAttribute('data-size-option-index'), 10);
    this.colourIndex = parseInt(modal.getAttribute('data-colour-option-index'), 10);
    this.productId = modal.getAttribute('data-product-id');
    this.variants = [];
    try {
      var node = modal.querySelector('[data-dv-sf-variants]');
      this.variants = node ? JSON.parse(node.textContent) : [];
    } catch (e) { this.variants = []; }
    this.unit = (readStore(STORAGE_UNIT).unit === 'in') ? 'in' : 'cm';
    this.values = this.loadValues();
    this.lastResult = null;
    this.build();
    this.bind();
    this.render();
    this.updateTrigger();
  }

  Finder.prototype.storeKey = function () {
    return this.cat.whoToMeasure === 'owner' ? STORAGE_OWNER : STORAGE_DOG;
  };

  Finder.prototype.loadValues = function () {
    var saved = readStore(this.storeKey());
    var out = {};
    var self = this;
    this.chart.measures.forEach(function (m) {
      if (isNum(saved[m])) out[m] = saved[m];
    });
    return out;
  };

  Finder.prototype.saveValues = function () {
    var saved = readStore(this.storeKey());
    var self = this;
    this.chart.measures.forEach(function (m) {
      if (isNum(self.values[m])) saved[m] = self.values[m];
      else delete saved[m];
    });
    saved.updatedAt = new Date().toISOString();
    writeStore(this.storeKey(), saved);
    writeStore(STORAGE_UNIT, { unit: this.unit });
  };

  /** Availability per normalized size, optionally within the selected colour. */
  Finder.prototype.availability = function () {
    var out = {};
    var sizeIdx = this.sizeIndex;
    var colourIdx = this.colourIndex;
    var colour = this.selectedColour();
    this.variants.forEach(function (v) {
      var opts = v.options || [v.option1, v.option2, v.option3];
      var size = normalizeSize(opts[sizeIdx]);
      if (!size) return;
      if (!out[size]) out[size] = { carried: true, available: false, anyColourAvailable: false };
      if (v.available) {
        out[size].anyColourAvailable = true;
        if (!colour || colourIdx < 0 || opts[colourIdx] === colour) out[size].available = true;
      }
    });
    return out;
  };

  Finder.prototype.selectedColour = function () {
    if (!(this.colourIndex >= 0)) return null;
    var form = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (!form) return null;
    var id = String(form.value);
    for (var i = 0; i < this.variants.length; i++) {
      if (String(this.variants[i].id) === id) {
        var opts = this.variants[i].options || [this.variants[i].option1, this.variants[i].option2, this.variants[i].option3];
        return opts[this.colourIndex] || null;
      }
    }
    return null;
  };

  Finder.prototype.build = function () {
    var self = this;
    var chart = this.chart;
    var inputs = this.modal.querySelector('[data-dv-sf-inputs]');
    inputs.innerHTML = '';
    this.inputEls = {};
    chart.measures.forEach(function (m) {
      var meta = MEASURES[m];
      var wrap = el('label', 'dv-sf-field');
      wrap.setAttribute('for', 'dv-sf-' + self.productId + '-' + m);
      var lab = el('span', 'dv-sf-field__label', meta.label);
      if (meta.diagram) {
        var badge = el('span', 'dv-sf-field__num', String(meta.diagram));
        badge.setAttribute('aria-hidden', 'true');
        lab.insertBefore(badge, lab.firstChild);
      }
      var row = el('span', 'dv-sf-field__row');
      var input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'decimal';
      input.step = 'any';
      input.min = '0';
      input.id = 'dv-sf-' + self.productId + '-' + m;
      input.setAttribute('data-measure', m);
      input.autocomplete = 'off';
      var suffix = el('span', 'dv-sf-field__unit', unitSuffix(self.unit, meta.unit));
      suffix.setAttribute('data-unit-for', m);
      row.appendChild(input);
      row.appendChild(suffix);
      var hint = el('span', 'dv-sf-field__hint', meta.hint);
      wrap.appendChild(lab);
      wrap.appendChild(row);
      wrap.appendChild(hint);
      inputs.appendChild(wrap);
      self.inputEls[m] = input;
    });
    this.fillInputs();
    this.buildMeasureTab();
    this.buildTableTab();
  };

  Finder.prototype.fillInputs = function () {
    var self = this;
    this.chart.measures.forEach(function (m) {
      var input = self.inputEls[m];
      input.value = fmt(self.values[m], self.unit, MEASURES[m].unit);
      var suffix = self.modal.querySelector('[data-unit-for="' + m + '"]');
      if (suffix) suffix.textContent = unitSuffix(self.unit, MEASURES[m].unit);
    });
    var toggles = this.modal.querySelectorAll('[data-dv-sf-unit]');
    for (var i = 0; i < toggles.length; i++) {
      var on = toggles[i].getAttribute('data-dv-sf-unit') === this.unit;
      toggles[i].classList.toggle('is-active', on);
      toggles[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  };

  Finder.prototype.buildMeasureTab = function () {
    var list = this.modal.querySelector('[data-dv-sf-measure-list]');
    if (!list) return;
    list.innerHTML = '';
    var self = this;
    this.chart.measures.forEach(function (m) {
      var meta = MEASURES[m];
      var li = el('li', 'dv-sf-steps__item');
      if (meta.diagram) li.appendChild(el('span', 'dv-sf-field__num', String(meta.diagram)));
      var body = el('span');
      body.appendChild(el('strong', null, meta.label + ': '));
      body.appendChild(document.createTextNode(meta.hint));
      li.appendChild(body);
      list.appendChild(li);
    });
    var intro = this.modal.querySelector('[data-dv-sf-measure-intro]');
    if (intro) {
      intro.textContent = this.cat.whoToMeasure === 'owner'
        ? 'Þessi flík er fyrir þig. Mældu sjálfa/n þig með málbandi yfir þunn föt.'
        : 'Láttu hundinn standa jafnt í alla fjóra fætur. Mældu þétt yfir feldinn og skildu eftir um fingurbreidd í bil. Aðeins ' + this.chart.measures.length + (this.chart.measures.length === 1 ? ' mál skiptir' : ' mál skipta') + ' máli fyrir þessa vöru.';
    }
    var diagram = this.modal.querySelector('[data-dv-sf-diagram]');
    if (diagram && this.cat.whoToMeasure === 'owner') diagram.hidden = true;
    var video = this.modal.querySelector('[data-dv-sf-video]');
    if (video && this.cat.whoToMeasure === 'owner') video.hidden = true;
  };

  Finder.prototype.buildTableTab = function () {
    var host = this.modal.querySelector('[data-dv-sf-table]');
    if (!host) return;
    host.innerHTML = '';
    var chart = this.chart;
    var unit = this.unit;
    var table = el('table', 'dv-sf-table');
    var thead = el('thead');
    var hr = el('tr');
    hr.appendChild(el('th', null, 'Stærð'));
    chart.measures.forEach(function (m) {
      hr.appendChild(el('th', null, MEASURES[m].label.replace(/ \(þitt\)$/, '') + ' (' + unitSuffix(unit, MEASURES[m].unit) + ')'));
    });
    if (chart.garmentLength) hr.appendChild(el('th', null, 'Sídd flíkur (cm)'));
    hr.appendChild(el('th', null, 'Hjá okkur'));
    thead.appendChild(hr);
    table.appendChild(thead);
    var tbody = el('tbody');
    var availability = this.availability();
    chart.sizes.forEach(function (row) {
      var tr = el('tr');
      tr.setAttribute('data-size', normalizeSize(row.size));
      tr.appendChild(el('th', null, sizeDisplay(chart, row.size)));
      chart.measures.forEach(function (m) {
        tr.appendChild(el('td', null, rangeText(row.ranges[m], unit, MEASURES[m].unit)));
      });
      if (chart.garmentLength) tr.appendChild(el('td', null, String(chart.garmentLength[row.size] || '')));
      var a = availability[normalizeSize(row.size)];
      var status = !a ? 'Ekki í úrvali' : (a.anyColourAvailable ? 'Til á lager' : 'Uppselt');
      var td = el('td', 'dv-sf-table__status dv-sf-table__status--' + (!a ? 'none' : (a.anyColourAvailable ? 'ok' : 'out')), status);
      tr.appendChild(td);
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    host.appendChild(table);

    var legend = this.modal.querySelector('[data-dv-sf-letters]');
    if (legend) {
      legend.innerHTML = '';
      if (chart.letters && chart.letters.length) {
        chart.letters.forEach(function (L) {
          var li = el('li');
          li.appendChild(el('strong', null, L + ': '));
          li.appendChild(document.createTextNode(FIT_LETTERS[L]));
          legend.appendChild(li);
        });
        legend.hidden = false;
      } else {
        legend.hidden = true;
      }
    }
    var advice = this.modal.querySelector('[data-dv-sf-advice]');
    if (advice) advice.textContent = this.cat.advice;
    var source = this.modal.querySelector('[data-dv-sf-source]');
    if (source) source.textContent = 'Heimild: ' + chart.source + '.';
  };

  Finder.prototype.bind = function () {
    var self = this;
    var m;
    for (m in this.inputEls) {
      (function (measure) {
        self.inputEls[measure].addEventListener('input', function () {
          var v = parseFloat(this.value.replace(',', '.'));
          self.values[measure] = isNum(v) && v > 0 ? toBase(v, self.unit, MEASURES[measure].unit) : null;
          self.saveValues();
          self.render();
        });
      })(m);
    }
    var toggles = this.modal.querySelectorAll('[data-dv-sf-unit]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].addEventListener('click', function () {
        var u = this.getAttribute('data-dv-sf-unit');
        if (u === self.unit) return;
        self.unit = u;
        self.saveValues();
        self.fillInputs();
        self.buildTableTab();
        self.render();
      });
    }
    var clear = this.modal.querySelector('[data-dv-sf-clear]');
    if (clear) {
      clear.addEventListener('click', function () {
        self.values = {};
        self.saveValues();
        self.fillInputs();
        self.render();
        var first = self.inputEls[self.chart.measures[0]];
        if (first) first.focus();
      });
    }
    var tabs = this.modal.querySelectorAll('[data-dv-sf-tab]');
    for (var t = 0; t < tabs.length; t++) {
      tabs[t].addEventListener('click', function () { self.showTab(this.getAttribute('data-dv-sf-tab')); });
    }
    var closers = this.modal.querySelectorAll('[data-dv-sf-close]');
    for (var c = 0; c < closers.length; c++) {
      closers[c].addEventListener('click', function () { self.close(); });
    }
    this.modal.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') self.close();
    });
    var triggers = document.querySelectorAll('[data-dv-sf-open]');
    for (var k = 0; k < triggers.length; k++) {
      triggers[k].addEventListener('click', function (ev) {
        ev.preventDefault();
        self.open(this.getAttribute('data-dv-sf-open') || 'calc');
      });
    }
    // Re-evaluate availability when the shopper changes colour.
    document.addEventListener('change', function (ev) {
      if (ev.target && ev.target.closest && ev.target.closest('variant-selects')) {
        self.buildTableTab();
        self.render();
      }
    });
  };

  Finder.prototype.showTab = function (name) {
    var tabs = this.modal.querySelectorAll('[data-dv-sf-tab]');
    var panels = this.modal.querySelectorAll('[data-dv-sf-panel]');
    for (var i = 0; i < tabs.length; i++) {
      var on = tabs[i].getAttribute('data-dv-sf-tab') === name;
      tabs[i].classList.toggle('is-active', on);
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-dv-sf-panel') !== name;
    }
  };

  Finder.prototype.open = function (tab) {
    this.modal.hidden = false;
    document.body.classList.add('dv-sf-open');
    this.showTab(tab || 'calc');
    var self = this;
    this.render();
    var focusTarget = tab === 'calc' || !tab ? this.inputEls[this.chart.measures[0]] : this.modal.querySelector('[data-dv-sf-close]');
    if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 30);
  };

  Finder.prototype.close = function () {
    this.modal.hidden = true;
    document.body.classList.remove('dv-sf-open');
    this.updateTrigger();
  };

  function pickAvailableVariant(variants, sizeIndex, colourIndex, size, selectedColour) {
    var target = normalizeSize(size);
    var available = (variants || []).filter(function (variant) {
      var options = variant.options || [variant.option1, variant.option2, variant.option3];
      return variant.available && normalizeSize(options[sizeIndex]) === target;
    });
    if (!available.length) return null;
    if (selectedColour && colourIndex >= 0) {
      for (var i = 0; i < available.length; i++) {
        var preferredOptions = available[i].options || [available[i].option1, available[i].option2, available[i].option3];
        if (preferredOptions[colourIndex] === selectedColour) return available[i];
      }
    }
    return available[0];
  }

  Finder.prototype.selectSize = function (size) {
    var variant = pickAvailableVariant(
      this.variants,
      this.sizeIndex,
      this.colourIndex,
      size,
      this.selectedColour()
    );
    if (!variant) {
      this.render();
      return false;
    }
    var picker = document.querySelector('variant-selects');
    var chosen = false;
    if (picker) {
      var options = variant.options || [variant.option1, variant.option2, variant.option3];
      var fieldsets = picker.querySelectorAll('fieldset');
      for (var i = 0; i < options.length; i++) {
        if (!options[i]) continue;
        var fieldset = fieldsets[i];
        if (!fieldset) continue;
        var radios = fieldset.querySelectorAll('input[type="radio"]');
        for (var r = 0; r < radios.length; r++) {
          if (radios[r].value === options[i]) {
            if (!radios[r].checked) radios[r].click();
            if (i === this.sizeIndex) chosen = true;
            break;
          }
        }
      }
      var selects = picker.querySelectorAll('select');
      if (selects.length) {
        for (var s = 0; s < selects.length && s < options.length; s++) {
          for (var o = 0; o < selects[s].options.length; o++) {
            if (selects[s].options[o].value === options[s]) {
              if (selects[s].value !== options[s]) {
                selects[s].value = options[s];
                selects[s].dispatchEvent(new Event('change', { bubbles: true }));
              }
              if (s === this.sizeIndex) chosen = true;
              break;
            }
          }
        }
      }
    }
    if (chosen) this.close();
    var anchor = picker || document.querySelector('[data-dv-sf-trigger]');
    if (anchor && anchor.scrollIntoView) anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return chosen;
  };

  Finder.prototype.render = function () {
    var host = this.modal.querySelector('[data-dv-sf-result]');
    if (!host) return;
    host.innerHTML = '';
    var chart = this.chart;
    var res = recommend(chart, this.values, this.availability());
    this.lastResult = res;
    var self = this;

    if (res.status === 'manual') {
      host.appendChild(el('p', 'dv-sf-result__title', 'Staðfestu stærðina áður en þú pantar'));
      host.appendChild(el('p', 'dv-sf-result__muted', 'Weekend Warrior er til í tveimur stærðarkerfum. Skoðaðu stærðartöfluna á þessari vörusíðu eða hafðu samband við okkur og við staðfestum rétta stærð.'));
      return;
    }

    if (res.status === 'empty') {
      host.appendChild(el('p', 'dv-sf-result__muted', 'Sláðu inn málin – niðurstaðan birtist um leið.'));
      return;
    }

    var missingNames = res.missing.map(function (m) { return MEASURES[m].label.toLowerCase(); });

    if (res.status === 'none' || res.status === 'partial-none') {
      host.appendChild(el('p', 'dv-sf-result__title', 'Engin stærð passar öllum málunum'));
      if (res.nearest.length) {
        var p = el('p', null, 'Næst lagi: ');
        res.nearest.forEach(function (e, i) {
          if (i) p.appendChild(document.createTextNode(' · '));
          p.appendChild(el('strong', null, sizeDisplay(chart, e.size)));
          var why = describeOutside(chart, e, self.unit);
          if (why) p.appendChild(document.createTextNode(' (' + why + ')'));
        });
        host.appendChild(p);
      }
      host.appendChild(el('p', 'dv-sf-result__muted', 'Athugaðu málin aftur – algengast er að brjóstmál sé tekið of aftarlega eða baklengd of langt fram. Hafðu annars samband, við hjálpum þér að velja.'));
      if (res.missing.length) host.appendChild(el('p', 'dv-sf-result__muted', 'Vantar enn: ' + missingNames.join(', ') + '.'));
      return;
    }

    var rec = res.recommended;
    var title = el('p', 'dv-sf-result__title');
    title.appendChild(document.createTextNode(res.status === 'partial' ? 'Líklegast stærð ' : 'Við mælum með stærð '));
    title.appendChild(el('strong', 'dv-sf-result__size', sizeDisplay(chart, rec.size)));
    host.appendChild(title);

    if (res.status === 'partial') {
      host.appendChild(el('p', 'dv-sf-result__muted', 'Sláðu líka inn ' + missingNames.join(' og ') + ' til að staðfesta.'));
    }

    var a = rec.availability;
    var statusText, statusCls;
    if (!a.carried) { statusText = 'Þessa stærð eigum við ekki í úrvali okkar.'; statusCls = 'none'; }
    else if (a.available) { statusText = 'Til á lager' + (this.selectedColour() ? ' í valda litnum.' : '.'); statusCls = 'ok'; }
    else if (a.anyColourAvailable) { statusText = 'Uppselt í valda litnum – til í öðrum lit.'; statusCls = 'warn'; }
    else { statusText = 'Uppselt í augnablikinu.'; statusCls = 'out'; }
    host.appendChild(el('p', 'dv-sf-result__status dv-sf-result__status--' + statusCls, statusText));

    if (rec.fallbackFrom) {
      host.appendChild(el('p', 'dv-sf-result__muted', 'Besta passformið samkvæmt töflu Hurtta væri ' + sizeDisplay(chart, rec.fallbackFrom.size) + ', sem er ekki í úrvali okkar. Stærð ' + sizeDisplay(chart, rec.size) + ' passar líka öllum málunum.'));
    }

    var why = describeFit(chart, rec);
    if (why) host.appendChild(el('p', 'dv-sf-result__muted', 'Athugið: ' + why + '.'));

    // Bars: where each measurement sits in the range.
    var bars = el('div', 'dv-sf-bars');
    chart.measures.forEach(function (m) {
      var p = rec.positions[m];
      if (!isNum(p)) return;
      var r = rec.row.ranges[m];
      var bar = el('div', 'dv-sf-bar');
      var head = el('div', 'dv-sf-bar__head');
      head.appendChild(el('span', null, MEASURES[m].label.replace(/ \(þitt\)$/, '')));
      head.appendChild(el('span', 'dv-sf-bar__range', rangeText(r, self.unit, MEASURES[m].unit) + ' ' + unitSuffix(self.unit, MEASURES[m].unit)));
      var track = el('div', 'dv-sf-bar__track');
      var marker = el('span', 'dv-sf-bar__marker');
      marker.style.left = (Math.max(0, Math.min(1, p)) * 100) + '%';
      marker.setAttribute('aria-hidden', 'true');
      track.appendChild(marker);
      var labels = el('div', 'dv-sf-bar__labels');
      labels.appendChild(el('span', null, 'þétt'));
      labels.appendChild(el('span', null, 'rúmt'));
      bar.appendChild(head);
      bar.appendChild(track);
      bar.appendChild(labels);
      bars.appendChild(bar);
    });
    host.appendChild(bars);

    if (a.carried && a.anyColourAvailable && res.status === 'ok') {
      var btnLabel = a.available
        ? 'Velja stærð ' + sizeDisplay(chart, rec.size)
        : 'Velja stærð ' + sizeDisplay(chart, rec.size) + ' í lit sem er til';
      var btn = el('button', 'button dv-sf-result__select', btnLabel);
      btn.type = 'button';
      btn.addEventListener('click', function () { self.selectSize(rec.size); });
      host.appendChild(btn);
    }

    if (res.alternatives.length) {
      var alt = el('div', 'dv-sf-alt');
      alt.appendChild(el('p', 'dv-sf-alt__title', 'Passar líka:'));
      var ul = el('ul', 'dv-sf-alt__list');
      res.alternatives.slice(0, 3).forEach(function (e) {
        var li = el('li');
        var b = el('button', 'dv-sf-alt__btn', sizeDisplay(chart, e.size));
        b.type = 'button';
        if (!e.availability.carried || !e.availability.anyColourAvailable) b.disabled = true;
        b.addEventListener('click', function () { self.selectSize(e.size); });
        li.appendChild(b);
        var d = describeFit(chart, e);
        var av = !e.availability.carried ? 'ekki í úrvali' : (e.availability.anyColourAvailable ? 'til' : 'uppselt');
        li.appendChild(document.createTextNode(' – ' + (d ? d + ', ' : '') + av));
        ul.appendChild(li);
      });
      alt.appendChild(ul);
      host.appendChild(alt);
    }

    host.appendChild(el('p', 'dv-sf-result__advice', this.cat.advice));
  };

  Finder.prototype.updateTrigger = function () {
    var summary = document.querySelector('[data-dv-sf-summary]');
    var card = document.querySelector('[data-dv-sf-trigger]');
    if (!summary) return;
    var res = recommend(this.chart, this.values, this.availability());
    if (res.status === 'ok' && res.recommended) {
      var size = sizeDisplay(this.chart, res.recommended.size);
      var availability = res.recommended.availability;
      if (availability.available) {
        summary.textContent = 'Samkvæmt vistuðum málum passar stærð ' + size + ' best og er til á lager. Opnaðu til að staðfesta málin og velja stærðina.';
        if (card) {
          card.classList.add('has-result');
          card.classList.remove('has-unavailable-result');
        }
      } else if (availability.anyColourAvailable) {
        summary.textContent = 'Samkvæmt vistuðum málum passar stærð ' + size + ' best. Hún er uppseld í valda litnum en til í öðrum lit.';
        if (card) {
          card.classList.add('has-result');
          card.classList.remove('has-unavailable-result');
        }
      } else {
        summary.textContent = 'Samkvæmt vistuðum málum passar stærð ' + size + ' best, en hún er uppseld í augnablikinu. Opnaðu til að yfirfara málin og fá aðstoð.';
        if (card) {
          card.classList.remove('has-result');
          card.classList.add('has-unavailable-result');
        }
      }
    } else if (card) {
      card.classList.remove('has-result');
      card.classList.remove('has-unavailable-result');
    }
  };

  function initAll() {
    var modals = document.querySelectorAll('[data-dv-sf-modal]');
    for (var i = 0; i < modals.length; i++) {
      if (modals[i].__dvSizeFinder) continue;
      modals[i].__dvSizeFinder = new Finder(modals[i]);
    }
  }

  return {
    MEASURES: MEASURES,
    CATEGORIES: CATEGORIES,
    CHARTS: CHARTS,
    FIT_LETTERS: FIT_LETTERS,
    normalizeSize: normalizeSize,
    evaluate: evaluate,
    recommend: recommend,
    describeFit: describeFit,
    describeOutside: describeOutside,
    pickAvailableVariant: pickAvailableVariant,
    initAll: initAll
  };
});
